import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksRepository } from "@/repositories";
import { employeesRepository } from "@/repositories";
import { leadsRepository } from "@/repositories/leads";
import { maintenanceRepository } from "@/repositories/maintenance";
import { propertiesRepository } from "@/repositories/properties";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLogTaskActivity } from "@/hooks/useTaskActivity";
import { reportTaskActivityMirrorFailure } from "@/lib/taskActivityMirror";
import { toast } from "sonner";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from "date-fns";

// Get employee record for the current user
export function useEmployeeRecord() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["employee-record", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      return employeesRepository.getRecordByUserId(user.id);
    },
    enabled: !!user?.id,
  });
}

// Tasks for the employee portal board/calendar. Normally the employee's OWN
// assigned tasks; D1 · a "see all tasks" property manager (permission
// `tasks.view_all`) gets the WHOLE company board instead — no admin promotion.
export function useEmployeeTasks() {
  const { data: employee } = useEmployeeRecord();
  const { hasPermission } = usePermissions();
  const seeAll = hasPermission("tasks.view_all");

  return useQuery({
    queryKey: ["employee-tasks", employee?.id, seeAll],
    queryFn: async () => {
      if (!employee?.id || !employee?.company_id) return [];
      const rows = seeAll
        ? await tasksRepository.listForUnifiedKanban(employee.company_id)
        : await tasksRepository.listMine(employee.id, employee.company_id);
      // Both are task rows, but the shapes differ: listMine returns EmployeeTaskRow
      // (property: { id, name, name_en, address }, plus `unit` and `contract`);
      // listForUnifiedKanban returns KanbanTaskRow (property: { name, name_en } only,
      // no unit/contract). The employee board/calendar read only the COMMON fields
      // (id, title, status, dates, property name), so a see-all row's `property?.id`,
      // `unit`, or `contract` are `undefined` — safe today, but a consumer that
      // reaches for those fields must handle the see-all case. Narrowed to the
      // richer listMine type for the callers; revisit if the board grows to need
      // the extra fields.
      return rows as unknown as Awaited<ReturnType<typeof tasksRepository.listMine>>;
    },
    enabled: !!employee?.id,
  });
}

/**
 * The leads tied to TASKS assigned to the signed-in employee (per-interest
 * pipeline "My Leads"). Auth-only; the employee is resolved server-side from the
 * JWT, so this takes no args. Follows the `useEmployee*` data-hook pattern so the
 * page component stays declarative.
 */
export function useAssignedLeads() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["employee-assigned-leads", user?.id],
    queryFn: () => leadsRepository.listAssignedToMe(),
    // Auth-gated (CLAUDE.md): the endpoint rejects unauthenticated callers, so
    // don't fire until the user is known.
    enabled: !!user?.id,
  });
}

// Update task status
export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: async ({ taskId, status }: { taskId: string; status: string }) => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const updateData: { status: string; completed_at?: string | null } = { status };

      if (status === "done") {
        updateData.completed_at = new Date().toISOString();
      } else {
        updateData.completed_at = null;
      }

      const data = await tasksRepository.update(taskId, companyId, updateData);
      if (!data) throw new Error("Task not found or access denied");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["employee-performance"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["unified-tasks"] });
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء تحديث حالة المهمة", "Error updating task status"));
    },
  });
}

// Shape of a single attachment carried alongside a task note
export interface TaskNoteAttachment {
  url: string;
  name: string;
  mime: string | null;
  size: number | null;
}

// Add note to task
export function useAddTaskNote() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();
  const logActivity = useLogTaskActivity();

  return useMutation({
    mutationFn: async ({
      taskId,
      note,
      attachments,
    }: {
      taskId: string;
      note: string;
      attachments?: TaskNoteAttachment[];
    }) => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const existingNotes = (await tasksRepository.getNotes(taskId, companyId)) || "";
      const timestamp = new Date().toLocaleString("en-US");
      const newNote = `[${timestamp}] ${note}`;
      const updatedNotes = existingNotes ? `${existingNotes}\n${newNote}` : newNote;

      const data = await tasksRepository.update(taskId, companyId, { notes: updatedNotes });
      if (!data) throw new Error("Task not found or access denied");

      try {
        const payload = {
          text: note,
          ...(attachments && attachments.length > 0
            ? { attachments }
            : {}),
        } as unknown as Parameters<typeof logActivity.mutateAsync>[0]["payload"];
        await logActivity.mutateAsync({
          task_id: taskId,
          kind: "note",
          payload,
        });
      } catch (err) {
        reportTaskActivityMirrorFailure("note", taskId, err);
      }

      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["employee-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task", vars.taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["unified-tasks"] });
      toast.success(t("تمت إضافة الملاحظة", "Note added successfully"));
    },
    onError: () => {
      toast.error(t("حدث خطأ أثناء إضافة الملاحظة", "Error adding note"));
    },
  });
}

// Get properties assigned to the employee via employee_property_assignments
export function useEmployeeProperties() {
  const { data: employee } = useEmployeeRecord();
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["employee-properties", employee?.id, companyId],
    queryFn: async () => {
      if (!employee?.id || !companyId) return [];

      // Get property IDs from assignments via the repository
      const assignments = await employeesRepository.listAssignments(employee.id, companyId);
      const propertyIds = assignments?.map(a => a.property_id).filter(Boolean) ?? [];

      if (propertyIds.length === 0) return [];

      return propertiesRepository.listWithUnitsByIdsRlsScoped(propertyIds);
    },
    enabled: !!employee?.id && !!companyId,
  });
}

// Get maintenance requests for employee's assigned properties
export function useEmployeeMaintenance() {
  const { data: properties } = useEmployeeProperties();
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["employee-maintenance", properties?.map(p => p.id)],
    queryFn: async () => {
      if (!properties || properties.length === 0 || !companyId) return [];

      // Nest-first via the oracle-scoped staff list endpoint.
      // The server resolves the employee's assigned properties from their JWT.
      const propertyIds = properties.map(p => p.id);
      return maintenanceRepository.listRequests({
        companyId,
        assignedPropertyIds: propertyIds,
      });
    },
    enabled: !!properties && properties.length > 0 && !!companyId,
  });
}

// Get employee performance metrics
export function useEmployeePerformance() {
  const { data: employee } = useEmployeeRecord();
  const { data: tasks } = useEmployeeTasks();

  return useQuery({
    queryKey: ["employee-performance", employee?.id, tasks?.length],
    queryFn: async () => {
      if (!tasks) return null;

      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 0 });
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      const completedTasks = tasks.filter(t => t.status === "done");
      const onHoldTasks = tasks.filter(t => t.status === "on_hold");
      const cancelledTasks = tasks.filter(t => t.status === "cancelled");
      const overdueTasks = tasks.filter(t => {
        if (!t.due_date) return false;
        if (t.status === "done" || t.status === "on_hold" || t.status === "cancelled") return false;
        const holdSeconds = Number((t as { on_hold_total_seconds?: number }).on_hold_total_seconds) || 0;
        const effectiveDue = new Date(new Date(t.due_date).getTime() + holdSeconds * 1000);
        return effectiveDue < now;
      });
      const activeTaskCount = tasks.filter(
        t => t.status === "todo" || t.status === "in_progress" || t.status === "done"
      ).length;

      const tasksCompletedThisWeek = completedTasks.filter(t => {
        if (!t.completed_at) return false;
        const completedDate = new Date(t.completed_at);
        return completedDate >= weekStart && completedDate <= weekEnd;
      });

      const tasksCompletedThisMonth = completedTasks.filter(t => {
        if (!t.completed_at) return false;
        const completedDate = new Date(t.completed_at);
        return completedDate >= monthStart && completedDate <= monthEnd;
      });

      let totalCompletionTime = 0;
      let completedWithTime = 0;

      completedTasks.forEach(task => {
        if (task.created_at && task.completed_at) {
          const created = new Date(task.created_at);
          const completed = new Date(task.completed_at);
          const holdSeconds = Number((task as { on_hold_total_seconds?: number }).on_hold_total_seconds) || 0;
          const diffDays = Math.max(
            0,
            (completed.getTime() - created.getTime() - holdSeconds * 1000) / (1000 * 60 * 60 * 24)
          );
          totalCompletionTime += diffDays;
          completedWithTime++;
        }
      });

      const avgCompletionTime = completedWithTime > 0 
        ? Math.round((totalCompletionTime / completedWithTime) * 10) / 10 
        : 0;

      const weeklyTrend = [];
      for (let i = 3; i >= 0; i--) {
        const weekStartDate = startOfWeek(subDays(now, i * 7), { weekStartsOn: 0 });
        const weekEndDate = endOfWeek(subDays(now, i * 7), { weekStartsOn: 0 });
        
        const count = completedTasks.filter(t => {
          if (!t.completed_at) return false;
          const completedDate = new Date(t.completed_at);
          return completedDate >= weekStartDate && completedDate <= weekEndDate;
        }).length;

        weeklyTrend.push({
          week: `أسبوع ${4 - i}`,
          completed: count,
        });
      }

      return {
        totalTasks: tasks.length,
        completedTasks: completedTasks.length,
        inProgressTasks: tasks.filter(t => t.status === "in_progress").length,
        todoTasks: tasks.filter(t => t.status === "todo").length,
        onHoldTasks: onHoldTasks.length,
        cancelledTasks: cancelledTasks.length,
        overdueTasks: overdueTasks.length,
        tasksCompletedThisWeek: tasksCompletedThisWeek.length,
        tasksCompletedThisMonth: tasksCompletedThisMonth.length,
        avgCompletionTime,
        completionRate: activeTaskCount > 0
          ? Math.round((completedTasks.length / activeTaskCount) * 100)
          : 0,
        weeklyTrend,
      };
    },
    enabled: !!employee?.id && !!tasks,
  });
}

// Get tasks by status for Kanban
export function useTasksByStatus() {
  const { data: tasks, isLoading } = useEmployeeTasks();

  const tasksByStatus = {
    todo: tasks?.filter(t => t.status === "todo") || [],
    in_progress: tasks?.filter(t => t.status === "in_progress") || [],
    on_hold: tasks?.filter(t => t.status === "on_hold") || [],
    done: tasks?.filter(t => t.status === "done") || [],
  };

  return { tasksByStatus, isLoading };
}

// Get tasks for calendar view
export function useTasksForCalendar() {
  const { data: tasks } = useEmployeeTasks();

  const calendarTasks = tasks?.filter(t => t.due_date).map(task => ({
    id: task.id,
    title: task.title,
    date: new Date(task.due_date!),
    priority: task.priority,
    status: task.status,
    property: task.property?.name,
  })) || [];

  return { calendarTasks };
}

// Get single task details
export function useTaskDetails(taskId: string | null) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: ["task-details", taskId, companyId],
    queryFn: async () => {
      if (!taskId || !companyId) return null;
      return tasksRepository.getDetailsById(taskId, companyId);
    },
    enabled: !!taskId && !!companyId,
  });
}

// Dashboard summary for employee
export function useEmployeeDashboard() {
  const { data: employee, isLoading: employeeLoading } = useEmployeeRecord();
  const { data: tasks, isLoading: tasksLoading } = useEmployeeTasks();
  const { data: performance } = useEmployeePerformance();

  const todayTasks = tasks?.filter(t => {
    if (!t.due_date) return false;
    const today = new Date();
    const dueDate = new Date(t.due_date);
    return dueDate.toDateString() === today.toDateString();
  }) || [];

  const upcomingTasks = tasks?.filter(t => {
    if (!t.due_date || t.status === "done") return false;
    const today = new Date();
    const dueDate = new Date(t.due_date);
    return dueDate > today;
  }).slice(0, 5) || [];

  return {
    employee,
    tasks,
    todayTasks,
    upcomingTasks,
    performance,
    isLoading: employeeLoading || tasksLoading,
  };
}
